import * as THREE from 'three';
import type { GameMap } from '@sunderfen/shared';

/**
 * Marks people leave: footprints pressed into sand, mud and snow that fade over a few seconds,
 * and rings spreading on the water when someone walks along the waterline.
 * Footprints are one instanced mesh drawn with multiply blending: white is "no mark", so a print
 * fades by drifting back to white.
 */
export class Traces {
  private prints: THREE.InstancedMesh;
  private printAge: Float32Array;
  private printDark: Float32Array;
  private next = 0;
  private rings: { m: THREE.Mesh; t: number; life: number }[] = [];
  private ringGeo = new THREE.RingGeometry(0.16, 0.22, 24).rotateX(-Math.PI / 2);
  private m = new THREE.Matrix4(); private q = new THREE.Quaternion(); private e = new THREE.Euler(); private v = new THREE.Vector3(); private s = new THREE.Vector3(); private c = new THREE.Color();

  constructor(private scene: THREE.Scene, private map: GameMap, count = 160) {
    const g = new THREE.CircleGeometry(0.5, 10).rotateX(-Math.PI / 2);
    const mat = new THREE.MeshBasicMaterial({ color: 0xffffff, blending: THREE.MultiplyBlending, transparent: true, premultipliedAlpha: true, depthWrite: false, polygonOffset: true, polygonOffsetFactor: -2, toneMapped: false, fog: false });
    this.prints = new THREE.InstancedMesh(g, mat, count);
    this.prints.frustumCulled = false; this.prints.renderOrder = 2;
    this.printAge = new Float32Array(count).fill(99); this.printDark = new Float32Array(count);
    for (let i = 0; i < count; i++) { this.prints.setMatrixAt(i, this.m.makeTranslation(0, -100, 0)); this.prints.setColorAt(i, this.c.setRGB(1, 1, 1)); }
    scene.add(this.prints);
  }

  /** One footprint. `side` −1 left, +1 right; `depth` 0..1 how soft the ground is. */
  footprint(x: number, z: number, face: number, side: number, depth: number) {
    const i = this.next; this.next = (this.next + 1) % this.printAge.length;
    const px = x + Math.cos(face) * side * 0.1, pz = z - Math.sin(face) * side * 0.1;
    this.e.set(0, face, 0); this.q.setFromEuler(this.e);
    this.prints.setMatrixAt(i, this.m.compose(this.v.set(px, this.map.heightAt(px, pz) + 0.02, pz), this.q, this.s.set(0.16, 1, 0.3)));
    this.printAge[i] = 0; this.printDark[i] = 0.35 + depth * 0.35;
    this.prints.instanceMatrix.needsUpdate = true;
  }

  /** A ring spreading on the water at (x, z). */
  ripple(x: number, z: number, big = false) {
    let r = this.rings.find(r => r.t >= r.life);
    if (!r) {
      if (this.rings.length >= 24) return;
      const m = new THREE.Mesh(this.ringGeo, new THREE.MeshBasicMaterial({ color: 0xe8f4f8, transparent: true, depthWrite: false, opacity: 0, toneMapped: false }));
      m.renderOrder = 3; this.scene.add(m);
      r = { m, t: 99, life: 1 }; this.rings.push(r);
    }
    r.m.position.set(x, 0.04, z); r.t = 0; r.life = big ? 1.6 : 1.1; r.m.visible = true;
  }

  update(dt: number) {
    let dirty = false;
    for (let i = 0; i < this.printAge.length; i++) {
      const a = this.printAge[i];
      if (a > 9) continue;
      this.printAge[i] = a + dt;
      const k = Math.max(0, 1 - this.printAge[i] / 8) * this.printDark[i];
      this.prints.setColorAt(i, this.c.setRGB(1 - k, 1 - k * 0.95, 1 - k * 0.9));
      if (this.printAge[i] > 9) this.prints.setMatrixAt(i, this.m.makeTranslation(0, -100, 0));
      dirty = true;
    }
    if (dirty) { this.prints.instanceColor!.needsUpdate = true; this.prints.instanceMatrix.needsUpdate = true; }
    for (const r of this.rings) {
      if (r.t >= r.life) { r.m.visible = false; continue; }
      r.t += dt;
      const k = r.t / r.life;
      r.m.scale.setScalar(1 + k * 4.5);
      (r.m.material as THREE.MeshBasicMaterial).opacity = (1 - k) * 0.55;
    }
  }
}
